import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Save, Loader2, Globe, Tag, Image, FileText, Mail, Bot, ExternalLink } from 'lucide-react';
import api from '@/lib/api';

export default function AdminSeoSettings() {
    const [settings, setSettings] = useState([]);
    const [values, setValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/site-settings');
            const data = res.data.data || [];
            setSettings(data);
            const initialValues = {};
            data.forEach(s => { initialValues[s.key] = s.value || ''; });
            setValues(initialValues);
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setSaved(false);
        try {
            const payload = Object.keys(values).map(key => ({
                key,
                value: values[key],
            }));
            await api.put('/admin/site-settings', { settings: payload });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch {
            // silent
        } finally {
            setSaving(false);
        }
    };

    const getIcon = (key) => {
        if (key.includes('title')) return <Globe className="h-4 w-4" />;
        if (key.includes('description')) return <FileText className="h-4 w-4" />;
        if (key.includes('keywords')) return <Tag className="h-4 w-4" />;
        if (key.includes('image')) return <Image className="h-4 w-4" />;
        if (key.includes('llms')) return <Bot className="h-4 w-4" />;
        return <Globe className="h-4 w-4" />;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
        );
    }

    const seoSettings = settings.filter(s => s.group === 'seo');
    const emailSettings = settings.filter(s => s.group === 'email');

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <h1 className="text-2xl font-bold uppercase tracking-wide">Configuración del Sitio</h1>
                <p className="text-white/60 text-sm mt-1">Administrá la configuración general del sistema.</p>
            </div>

            {/* SEO Settings */}
            <Card className="border-hairline-dark bg-[#0a0a0a]">
                <CardHeader>
                    <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        SEO y Metadatos
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                    {seoSettings.map(setting => (
                        <div key={setting.key} className="space-y-1.5">
                            <label className="flex items-center gap-2 text-xs text-white/60 uppercase tracking-wider">
                                {getIcon(setting.key)}
                                {setting.label}
                            </label>
                            {setting.type === 'text' ? (
                                <Textarea
                                    value={values[setting.key] || ''}
                                    onChange={(e) => setValues(prev => ({ ...prev, [setting.key]: e.target.value }))}
                                    placeholder={setting.description}
                                    rows={setting.key.includes('llms') ? 16 : 3}
                                    className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark resize-none"
                                />
                            ) : (
                                <Input
                                    value={values[setting.key] || ''}
                                    onChange={(e) => setValues(prev => ({ ...prev, [setting.key]: e.target.value }))}
                                    placeholder={setting.description}
                                    className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                                />
                            )}
                            <p className="text-[10px] text-white/40">{setting.description}</p>
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* SEO Preview */}
            <Card className="border-hairline-dark bg-[#0a0a0a]">
                <CardHeader>
                    <CardTitle className="text-sm font-bold uppercase tracking-wider">Vista Previa en Buscadores</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-1 p-4 rounded-lg bg-white/5">
                        <p className="text-[#8ab4f8] text-sm truncate">{values.site_title || 'GYMS.ar'}</p>
                        <p className="text-[#dadce0] text-xs truncate">https://gymcard.app</p>
                        <p className="text-[#bdc1c6] text-xs line-clamp-2">{values.meta_description || '...'}</p>
                    </div>
                </CardContent>
            </Card>

            {/* LLMs.txt Preview */}
            <Card className="border-hairline-dark bg-[#0a0a0a]">
                <CardHeader>
                    <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                        <Bot className="h-4 w-4" />
                        LLMs.txt
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-xs text-white/40">
                        Archivo público <code className="text-white/60">/llms.txt</code> optimizado para indexación por modelos de lenguaje (LLMs) y motores de búsqueda con IA.
                    </p>
                    <a
                        href="/llms.txt"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs text-white/80 hover:text-white underline"
                    >
                        <ExternalLink className="h-3 w-3" />
                        Ver /llms.txt en vivo
                    </a>
                </CardContent>
            </Card>

            {/* Email Settings */}
            <Card className="border-hairline-dark bg-[#0a0a0a]">
                <CardHeader>
                    <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Configuración de Email
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                    <p className="text-xs text-white/40 mb-2">Configurá la dirección y nombre desde la cual se envían todos los emails del sistema.</p>
                    {emailSettings.map(setting => (
                        <div key={setting.key} className="space-y-1.5">
                            <label className="flex items-center gap-2 text-xs text-white/60 uppercase tracking-wider">
                                <Mail className="h-4 w-4" />
                                {setting.label}
                            </label>
                            <Input
                                value={values[setting.key] || ''}
                                onChange={(e) => setValues(prev => ({ ...prev, [setting.key]: e.target.value }))}
                                placeholder={setting.description}
                                className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                            />
                            <p className="text-[10px] text-white/40">{setting.description}</p>
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Save Button */}
            <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full uppercase tracking-widest text-xs max-w-3xl"
            >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Save className="h-4 w-4 mr-2" />
                Guardar Cambios
            </Button>
            {saved && (
                <p className="text-center text-xs text-green-400 mt-2">Configuración guardada correctamente.</p>
            )}
        </div>
    );
}
